import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { fetchpages, fetchsettings } from "../Reducers/actions";
import { message } from "../../../Reducers/loading/actions";
import moment from "moment";

const WebsiteStructure = ({ pages, settings, fetchpages, fetchsettings, message }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPage, setSelectedPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [websiteStructure, setWebsiteStructure] = useState([]);
  const [stats, setStats] = useState({
    totalPages: 0,
    mainPages: 0,
    subPages: 0,
    components: 0
  });

  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchsettings(),
          fetchpages()
        ]);
        setLoading(false);
      } catch (error) {
        message(4000, "danger", "Fout bij laden van website structuur");
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  useEffect(() => {
    if (settings?.website && pages) {
      buildWebsiteStructure();
      calculateStats();
    }
  }, [settings, pages, searchTerm]);

  const buildWebsiteStructure = () => {
    if (!settings?.website || !pages) return;

    const structure = settings.website.map(mainPage => {
      // Find the actual page data
      const pageData = pages.find(p => p.naam === mainPage.naam || p.slug === mainPage.slug);
      
      const mainNode = {
        id: `main-${mainPage.slug}`,
        type: 'main',
        naam: mainPage.naam,
        slug: mainPage.slug,
        pageData: pageData,
        children: []
      };

      // Add subpages if they exist
      if (mainPage.sub && mainPage.sub.length > 0) {
        mainNode.children = mainPage.sub.map(subPage => {
          const subSlug = `${mainPage.slug}${subPage.slug}`;
          const subPageData = pages.find(p => p.naam === `${mainPage.naam}-${subPage.naam}` || p.slug === subSlug);
          
          const subNode = {
            id: `sub-${subSlug}`,
            type: 'sub',
            naam: subPage.naam,
            slug: subSlug,
            parent: mainPage.naam,
            pageData: subPageData,
            children: []
          };

          // Add sub-sub pages if they exist
          if (subPage.sub && subPage.sub.length > 0) {
            subNode.children = subPage.sub.map(subSubPage => {
              const subSubSlug = `${subSlug}${subSubPage.slug}`;
              const subSubPageData = pages.find(p => p.slug === subSubSlug);
              
              return {
                id: `subsub-${subSubSlug}`,
                type: 'subsub',
                naam: subSubPage.naam,
                slug: subSubSlug,
                parent: `${mainPage.naam}-${subPage.naam}`,
                pageData: subSubPageData,
                children: []
              };
            });
          }

          return subNode;
        });
      }

      return mainNode;
    });

    setWebsiteStructure(structure);
  };

  const calculateStats = () => {
    if (!settings?.website || !pages) return;

    let totalPages = 0;
    let mainPages = settings.website.length;
    let subPages = 0;

    settings.website.forEach(mainPage => {
      totalPages++; // Count main page
      if (mainPage.sub) {
        mainPage.sub.forEach(subPage => {
          totalPages++; // Count sub page
          subPages++;
          if (subPage.sub) {
            totalPages += subPage.sub.length; // Count sub-sub pages
            subPages += subPage.sub.length;
          }
        });
      }
    });

    const components = pages.filter(p => p.pageType === "onderdeel").length;

    setStats({
      totalPages,
      mainPages,
      subPages,
      components
    });
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const expandAll = () => {
    const allNodeIds = new Set();
    const collectIds = (nodes) => {
      nodes.forEach(node => {
        if (node.children.length > 0) {
          allNodeIds.add(node.id);
          collectIds(node.children);
        }
      });
    };
    collectIds(websiteStructure);
    setExpandedNodes(allNodeIds);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const getPageStatus = (pageData) => {
    if (!pageData) return { status: 'missing', badge: 'danger', text: 'Niet gevonden' };
    if (pageData.status === 'published') return { status: 'published', badge: 'success', text: 'Gepubliceerd' };
    if (pageData.status === 'draft') return { status: 'draft', badge: 'warning', text: 'Concept' };
    return { status: 'draft', badge: 'warning', text: 'Concept' };
  };

  const getNodeIcon = (node) => {
    switch (node.type) {
      case 'main':
        return 'fa-home';
      case 'sub':
        return 'fa-folder';
      case 'subsub':
        return 'fa-file-alt';
      default:
        return 'fa-file';
    }
  };

  const getNodeLevel = (type) => {
    switch (type) {
      case 'main': return 0;
      case 'sub': return 1;
      case 'subsub': return 2;
      default: return 0;
    }
  };

  const filteredStructure = websiteStructure.filter(node => {
    if (!searchTerm) return true;
    
    const searchNode = (node) => {
      if (node.naam.toLowerCase().includes(searchTerm.toLowerCase())) return true;
      return node.children.some(child => searchNode(child));
    };
    
    return searchNode(node);
  });

  const renderNode = (node, level = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children.length > 0;
    const statusInfo = getPageStatus(node.pageData);
    const paddingLeft = level * 2;

    return (
      <div key={node.id} className="website-node">
        <div 
          className={`node-item ${selectedPage === node.id ? 'selected' : ''} level-${level}`}
          style={{ paddingLeft: `${paddingLeft}rem` }}
          onClick={() => setSelectedPage(node.id)}
        >
          <div className="node-content">
            <div className="node-toggle">
              {hasChildren ? (
                <button
                  className="btn btn-sm btn-link p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleNode(node.id);
                  }}
                >
                  <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                </button>
              ) : (
                <span className="node-spacer"></span>
              )}
            </div>
            
            <div className="node-icon">
              <i className={`fa ${getNodeIcon(node)} text-primary`}></i>
            </div>
            
            <div className="node-info flex-grow-1">
              <div className="node-title">
                {node.pageData ? (
                  <Link 
                    to={`/cms/content/${node.pageData._id}`}
                    className="text-decoration-none fw-semibold"
                  >
                    {node.naam}
                  </Link>
                ) : (
                  <span className="text-muted">{node.naam}</span>
                )}
                
                <div className="node-path">
                  <small className="text-muted">/{node.slug}</small>
                </div>
              </div>
            </div>
            
            <div className="node-actions">
              <span className={`badge bg-${statusInfo.badge} me-2`}>
                {statusInfo.text}
              </span>
              
              {node.pageData && (
                <small className="text-muted">
                  {moment(node.pageData.updated).fromNow()}
                </small>
              )}
              
              <div className="dropdown ms-2">
                <button 
                  className="btn btn-sm btn-outline-secondary dropdown-toggle"
                  data-bs-toggle="dropdown"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="fa fa-ellipsis-h"></i>
                </button>
                <ul className="dropdown-menu">
                  {node.pageData ? (
                    <>
                      <li>
                        <Link 
                          className="dropdown-item" 
                          to={`/cms/content/${node.pageData._id}`}
                        >
                          <i className="fa fa-edit me-2"></i>Bewerken
                        </Link>
                      </li>
                      <li>
                        <button className="dropdown-item">
                          <i className="fa fa-eye me-2"></i>Bekijken
                        </button>
                      </li>
                    </>
                  ) : (
                    <li>
                      <button 
                        className="dropdown-item"
                        onClick={() => navigate(`/cms/content/new?slug=${node.slug}&naam=${node.naam}&pageType=pagina`)}
                      >
                        <i className="fa fa-plus me-2"></i>Pagina Aanmaken
                      </button>
                    </li>
                  )}
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button className="dropdown-item">
                      <i className="fa fa-plus me-2"></i>Subpagina Toevoegen
                    </button>
                  </li>
                  <li>
                    <button className="dropdown-item text-danger">
                      <i className="fa fa-trash me-2"></i>Verwijderen
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        {hasChildren && isExpanded && (
          <div className="node-children">
            {node.children.map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="website-structure">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status"></div>
            <h5 className="text-muted">Website structuur laden...</h5>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="website-structure">
      {/* Header */}
      <div className="structure-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="h2 mb-1">Website Structuur</h1>
            <p className="text-muted mb-0">Beheer je website hiërarchie en pagina structuur</p>
          </div>
          <div className="d-flex gap-2">
            <Link to="/cms/types/pages" className="btn btn-outline-primary">
              <i className="fa fa-sitemap me-2"></i>
              Structuur Bewerken
            </Link>
            <button className="btn btn-success">
              <i className="fa fa-plus me-2"></i>
              Nieuwe Pagina
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card stats-card bg-primary text-white">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <div className="h4 mb-1">{stats.totalPages}</div>
                  <div className="small">Totaal Pagina's</div>
                </div>
                <div className="icon-container">
                  <i className="fa fa-file-alt fa-2x opacity-75"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card stats-card bg-success text-white">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <div className="h4 mb-1">{stats.mainPages}</div>
                  <div className="small">Hoofdpagina's</div>
                </div>
                <div className="icon-container">
                  <i className="fa fa-home fa-2x opacity-75"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card stats-card bg-warning text-white">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <div className="h4 mb-1">{stats.subPages}</div>
                  <div className="small">Subpagina's</div>
                </div>
                <div className="icon-container">
                  <i className="fa fa-folder fa-2x opacity-75"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card stats-card bg-info text-white">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <div className="h4 mb-1">{stats.components}</div>
                  <div className="small">Componenten</div>
                </div>
                <div className="icon-container">
                  <i className="fa fa-puzzle-piece fa-2x opacity-75"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fa fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Zoek in website structuur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex gap-2 justify-content-end">
                <button 
                  className="btn btn-outline-secondary"
                  onClick={expandAll}
                >
                  <i className="fa fa-expand-arrows-alt me-2"></i>
                  Alles Uitklappen
                </button>
                <button 
                  className="btn btn-outline-secondary"
                  onClick={collapseAll}
                >
                  <i className="fa fa-compress-arrows-alt me-2"></i>
                  Alles Inklappen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Website Tree Structure */}
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="fa fa-sitemap me-2"></i>
            Website Hiërarchie
          </h5>
        </div>
        <div className="card-body p-0">
          <div className="website-tree">
            {filteredStructure.length > 0 ? (
              filteredStructure.map(node => renderNode(node))
            ) : (
              <div className="text-center py-5">
                <i className="fa fa-sitemap fa-3x text-muted mb-3"></i>
                <h4 className="text-muted">Geen website structuur gevonden</h4>
                <p className="text-muted">
                  {searchTerm ? "Probeer een andere zoekterm" : "Configureer je website structuur in de instellingen"}
                </p>
                <Link to="/cms/settings/website" className="btn btn-primary">
                  <i className="fa fa-cog me-2"></i>
                  Website Configureren
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Components Section */}
      {settings?.onderdelen && settings.onderdelen.length > 0 && (
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fa fa-puzzle-piece me-2"></i>
              Herbruikbare Componenten
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              {settings.onderdelen.map((component, index) => {
                const componentData = pages.find(p => p.naam === component.onderdeel && p.pageType === "onderdeel");
                const statusInfo = getPageStatus(componentData);
                
                return (
                  <div key={index} className="col-md-6 col-lg-4 mb-3">
                    <div className="card component-item">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <h6 className="card-title">
                              {componentData ? (
                                <Link 
                                  to={`/cms/content/${componentData._id}`}
                                  className="text-decoration-none"
                                >
                                  {component.onderdeel}
                                </Link>
                              ) : (
                                <span className="text-muted">{component.onderdeel}</span>
                              )}
                            </h6>
                            <small className="text-muted">Herbruikbaar component</small>
                          </div>
                          <span className={`badge bg-${statusInfo.badge}`}>
                            {statusInfo.text}
                          </span>
                        </div>
                        {componentData && (
                          <div className="mt-2">
                            <small className="text-muted">
                              Gewijzigd {moment(componentData.updated).fromNow()}
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  pages: state.cms.pages.items,
  settings: state.cms.settings
});

export default connect(mapStateToProps, {
  fetchpages,
  fetchsettings,
  message
})(WebsiteStructure);